import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { styles } from "../../styles/pages/Category/CategoryEditStyles";
import Loader from "../../components/common/Loader";

const CategoryEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prodCatName, setProdCatName] = useState("");
  const [prodCatDesc, setProdCatDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{
    prod_cat_name?: string;
    prod_cat_desc?: string;
  }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  useEffect(() => {
    const fetchCategory = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token || !id) {
        setErrors({
          prod_cat_name:
            "Authorization or category ID required. Please log in or select a category.",
        });
        setLoading(false);
        navigate("/signin");
        return;
      }
      try {
        const formData = new FormData();
        formData.append("pro_cat_id", id);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/category/edit`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (
          response.data.status === 200 &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const categoryData = response.data.data[0]; // Access the first item in the array
          setProdCatName(categoryData.prod_cat_name || "");
          setProdCatDesc(categoryData.prod_cat_desc || "");
        } else {
          setErrors({
            prod_cat_name:
              "Failed to load category data. Status: " + response.data.status,
          });
        }
      } catch (error: any) {
        console.error("Fetch error:", error.response?.data || error.message);
        setErrors({
          prod_cat_name:
            error.response?.data?.message || "An unexpected error occurred.",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    if (!prodCatName.trim()) {
      setErrors({ prod_cat_name: "The Category name field is required" });
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");
    const userId = sessionStorage.getItem("user_id");

    if (!token || !userId || !id) {
      setErrors({
        prod_cat_name:
          "Authorization, User ID, or category ID required. Please log in.",
      });
      setSaving(false);
      navigate("/signin");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("pro_cat_id", id);
      formData.append("prod_cat_name", prodCatName);
      formData.append("prod_cat_desc", prodCatDesc);
      formData.append("user_id", userId);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/category/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/category-list");
        }, 2000);
      } else if (response.data.status === 422) {
        setErrors(
          response.data.message || { prod_cat_name: "Validation error" }
        );
      } else {
        setErrors({ prod_cat_name: "Update failed" });
      }
    } catch (error: any) {
      console.error("Update error:", error.response?.data || error.message);
      setErrors({
        prod_cat_name:
          error.response?.data?.message || "An unexpected error occurred.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "prod_cat_name") {
      setProdCatName(value);
      if (!value.trim()) {
        setErrors({
          ...errors,
          prod_cat_name: "The Category name field is required",
        });
      } else {
        setErrors({ ...errors, prod_cat_name: undefined });
      }
    } else if (name === "prod_cat_desc") {
      setProdCatDesc(value);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className="flex items-center space-x-4">
          <h2 className={styles.title}>Edit Category</h2>
        </div>
      </div>
      {loading && <Loader />}
      {!loading && (
        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <div>
                <label className={styles.label}>
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prod_cat_name"
                  value={prodCatName}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.prod_cat_name
                      ? styles.inputError
                      : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                  }`}
                />
                {errors.prod_cat_name && (
                  <span className={styles.errorText}>
                    {errors.prod_cat_name}
                  </span>
                )}
              </div>
              <div>
                <label className={styles.label}>Category Description</label>
                <textarea
                  name="prod_cat_desc"
                  value={prodCatDesc}
                  onChange={handleInputChange}
                  className={`${styles.input} border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 dark:focus:border-brand-800`}
                />
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <button
                type="submit"
                disabled={saving}
                className={styles.submitButton}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/category-list")}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </form>
          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
          >
            <div className="flex justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
              Category Updated Successfully
            </h4>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setSuccessModalOpen(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default CategoryEdit;
